import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, AuthResponse, TokenPair, UserKind } from 'shared';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  // kind discriminates which login flow produced the stored tokens so the API
  // client can route refresh/logout to the matching endpoint tree.
  kind: UserKind | null;

  // Actions
  setAuth: (response: AuthResponse, kind?: UserKind) => void;
  setTokens: (tokens: TokenPair) => void;
  setUser: (user: User) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      kind: null,

      setAuth: (response: AuthResponse, kind: UserKind = 'staff') =>
        set({
          user: response.user,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          isAuthenticated: true,
          kind,
        }),

      setTokens: (tokens: TokenPair) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        }),

      setUser: (user: User) =>
        set({ user }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          kind: null,
        }),

      setLoading: (loading: boolean) =>
        set({ isLoading: loading }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        kind: state.kind,
      }),
    }
  )
);
